// cne-facts.js — the SINGLE source of truth for every Hong Kong CNE
// regulatory figure rendered anywhere in the product.
//
// ─────────────────────────────────────────────────────────────────────────
//  WHY THIS FILE IS A PLAIN CLASSIC SCRIPT (not ESM, not .jsx)
//
//  It has to load identically in THREE runtimes:
//    1. the app bundle  — esbuild concatenates src/entry.js into one IIFE
//                         (build.mjs); module scope is NOT shared, so the
//                         trailing Object.assign(window, …) is what makes
//                         `CNE_FACTS` resolve as a bare global everywhere.
//    2. the site build  — site/build.mjs pre-renders each page inside a
//                         node:vm sandbox where `sandbox.window = sandbox`
//                         and each script is run with vm.runInContext; the
//                         same window assignment is what publishes it.
//    3. the browser     — the generated site pages load it as a classic
//                         <script src="/cne-facts.js"> before cne-shared.js.
//
//  ‼️ The trailing `Object.assign(window, { CNE_FACTS })` is LOad-BEARING.
//  This is a .js file, so it is NOT covered by build.mjs's JSX auto-expose
//  plugin. Delete that line and esbuild tree-shakes the whole module out of
//  the bundle → `CNE_FACTS is not defined` at boot → blank page. The guard
//  is 'CNE_FACTS' in build.mjs's REQUIRED_GLOBALS, which fails the build
//  loudly instead. Do not remove either.
//
//  Source: NCHK letter "Commencement of the first Mandatory CNE Cycle"
//  (30 Jun 2026), verified against the source PDF (2026-07-09). Do not edit a
//  number on a page or a screen — edit it HERE. See
//  project_cne_mandatory_cycle_rule.
//
//  Every human-readable value is a { en, zh } pair so both the app's
//  L(value, lang) helper (data.js) and the site's own lang switch can render
//  it without conversion. Keep that shape for anything new.
// ─────────────────────────────────────────────────────────────────────────
const CNE_FACTS = {
  rnPoints: 45, // Registered Nurse: 45 CNE points / 3-year cycle
  enPoints: 30, // Enrolled Nurse: 30 CNE points / 3-year cycle
  cycleYears: 3,
  hoursPerPoint: 1, // 1 hour of learning activity = 1 CNE point
  indirectCap: 5, // max 5 points/cycle from programmes indirectly related to nursing
  retentionYears: 6, // CNE records retained ≥ 6 years
  blsPoints: 4.5, // AHA BLS Provider = 4.5 CNE points, valid 2 years
  aclsPalsRange: { zh: '一般為 12–16 分', en: 'typically 12–16 points' },
  pcValidYears: 3, // Practising Certificate valid 3 years
  pcExpiry: { zh: '第三年的 1 月 1 日至 12 月 31 日', en: '1 January to 31 December of the third year' },
  // Full dates use the standard Chinese no-space form (2026年7月1日).
  // Cycle = renewal_date − 6 months, 3 years; standard cohort begins 1 July.
  cycleStart: { zh: '每年7月1日', en: '1 July each year' },
  mandatoryEffective: { zh: '2026年7月1日', en: '1 July 2026' },
  firstCycleEnds: { zh: '2029年6月30日', en: '30 June 2029' },
  firstEnforcement: { zh: '2030–2032年度執業證書', en: 'the 2030–2032 practising certificate' },
  // First certificate BOUND by mandatory CNE = the one expiring on this date
  // (letter: "practising certificate expiring on 31 December 2029"; renewals
  // due earlier remain voluntary). This is now DETERMINED, not projected — the
  // Council's letter of `letterDate` fixed it; copy citing these facts should
  // not hedge with 預計/expected.
  firstMandatoryExpiry: { zh: '2029年12月31日', en: '31 December 2029' },
  // Machine-readable form of firstMandatoryExpiry, used by licenseIsMandatory
  // (data.js) to gate the legal-requirement framing per licence.
  //
  // ‼️ MIRRORED in supabase/functions/notify-cron/index.ts as a literal.
  // That function runs in Deno with no bundler and cannot import this file, so
  // the two copies can only be kept honest by a test: scripts/test-data.mjs
  // reads the Deno source as TEXT and asserts the literal matches this value.
  // If you change this date, change it there too — the test will tell you.
  mandatoryFrom: '2029-12-31',
  letterDate: { zh: '2026年6月30日', en: '30 June 2026' },
  // New operational requirement from the same letter: nurses must select ONE
  // accredited CNE Administrator to establish a personal CNE database for
  // future renewal applications. The accredited list IS published (transcribed
  // into `administrators` below, verified 2026-07-27) — but the Mandatory CNE
  // System manual is still unpublished, so HOW a nurse registers with one, what
  // it costs and whether a choice can be changed are all still unknown. Pair any
  // statement about the PROCESS with pendingNote; the NAMES themselves are firm.
  cneAdministrator: {
    zh: '護士須從護士管理局公布的認可名單中，選擇一間 CNE Administrator 建立個人 CNE 資料庫，作日後續領執業證書之用',
    en: 'Nurses must select one organisation from the NCHK’s published list of accredited CNE Administrators to establish a personal CNE database for future practising-certificate renewals',
  },
  cneAdministratorListUrl: 'https://www.nchk.org.hk/en/continuing_nursing_education/list_of_accredited_administrators_of_cne/index.html',
  // The ACTUAL accredited list, transcribed verbatim from the Nursing Council's
  // PDF "Administrators of Continuing Nursing Education (CNE) under the
  // Mandatory CNE System, Accredited by the Nursing Council of Hong Kong
  // (as at 1 July 2026)", read 2026-07-27:
  //   https://www.nchk.org.hk/filemanager/en/pdf/list_accredited_Administrators_CNE_en.pdf
  //
  // ‼️ NEVER add, rename or "tidy" an entry here from memory. These are the
  // eight bodies a nurse may legally register her CNE database with; an invented
  // or mistyped name sends her to the wrong organisation. Re-transcribe from the
  // PDF and update `administratorsAsAt` when the Council republishes.
  //
  // name_zh is null wherever the organisation's official Chinese name could not
  // be confirmed: the Council publishes this list in ENGLISH ONLY (its own
  // Chinese page states「暫只備有英文版」), so a Chinese rendering here would be
  // our translation, not the accredited name. The UI falls back to name_en.
  administratorsAsAt: { zh: '2026年7月1日', en: '1 July 2026' },
  administratorsPeriod: { zh: '2026年7月1日至2031年6月30日', en: '1 Jul 2026 – 30 Jun 2031' },
  administratorsSourceUrl: 'https://www.nchk.org.hk/filemanager/en/pdf/list_accredited_Administrators_CNE_en.pdf',
  administrators: [
    { key: 'ha',      name_en: 'Hospital Authority',                                  name_zh: '醫院管理局' },
    { key: 'dh',      name_en: 'Department of Health',                                name_zh: '衞生署' },
    { key: 'ahkns',   name_en: 'Association of Hong Kong Nursing Staff',              name_zh: '香港護士協會' },
    { key: 'conhk',   name_en: 'College of Nursing, Hong Kong',                       name_zh: '香港護理學院' },
    { key: 'csd',     name_en: 'Health Care Section, Correctional Services Department', name_zh: null },
    { key: 'hkanm',   name_en: 'The Hong Kong Academy of Nursing & Midwifery Limited', name_zh: null },
    { key: 'nupro',   name_en: 'Nu Pro Nurse Centre',                                 name_zh: null },
    { key: 'hkag',    name_en: 'Hong Kong Association of Gerontology',                name_zh: '香港老年學會' },
  ],
  billGazetted: { zh: '2023年12月8日', en: '8 December 2023' },
  // Regulatory timeline — what changed, when, and what it means for a nurse.
  //
  // Hong Kong nurses have no channel that proactively tells them the rules
  // moved; this list plus the `regulatory` broadcast is that channel. Rendered
  // by cne-info.jsx and available to the site.
  //
  // ‼️ EVERY entry must be a DATED, PUBLISHED fact with a source. `date` is
  // ISO so it sorts and so the broadcast can reference an entry by id. Do not
  // add rumours, "expected" dates, or anything without a URL — an unreliable
  // timeline is worse than no timeline in a compliance product.
  timeline: [
    {
      id: 'bill-gazetted-2023',
      date: '2023-12-08',
      title: { zh: '修例草案刊憲', en: 'Enabling bill gazetted' },
      note: {
        zh: '為強制性持續護理教育提供法律基礎的條例草案刊憲。',
        en: 'The bill providing the legal basis for mandatory CNE was gazetted.',
      },
      sourceUrl: 'https://www.nchk.org.hk',
    },
    {
      id: 'nchk-letter-2026',
      date: '2026-06-30',
      title: { zh: '護士管理局公布首個強制週期', en: 'NCHK confirms the first mandatory cycle' },
      note: {
        zh: '護士管理局來信確認強制 CNE 由 2026年7月1日生效,首個週期至 2029年6月30日,並要求護士選定認可 CNE Administrator。',
        en: 'The Council’s letter confirmed mandatory CNE from 1 July 2026, a first cycle ending 30 June 2029, and the requirement to select an accredited CNE Administrator.',
      },
      sourceUrl: 'https://www.nchk.org.hk',
    },
    {
      id: 'mandatory-start-2026',
      date: '2026-07-01',
      title: { zh: '強制性 CNE 生效', en: 'Mandatory CNE takes effect' },
      note: {
        zh: '首個強制 CNE 週期開始。此日之後取得的學分計入首個週期。',
        en: 'The first mandatory CNE cycle begins. Points earned from this date count toward it.',
      },
      sourceUrl: 'https://www.nchk.org.hk',
    },
    {
      id: 'administrators-2026',
      date: '2026-07-01',
      title: { zh: '認可 CNE Administrator 名單公布', en: 'Accredited CNE Administrator list published' },
      note: {
        zh: '護士管理局公布 8 間認可 CNE Administrator,認可期至 2031年6月30日。登記程序及費用尚未公布。',
        en: 'The Council published 8 accredited CNE Administrators, accredited to 30 June 2031. Registration process and fees are not yet published.',
      },
      sourceUrl: 'https://www.nchk.org.hk/filemanager/en/pdf/list_accredited_Administrators_CNE_en.pdf',
    },
    {
      id: 'first-cycle-ends-2029',
      date: '2029-06-30',
      title: { zh: '首個週期結束', en: 'First cycle ends' },
      note: {
        zh: '首個強制 CNE 週期的學分限期。',
        en: 'The earning deadline for the first mandatory CNE cycle.',
      },
      sourceUrl: 'https://www.nchk.org.hk',
    },
    {
      id: 'first-bound-pc-2029',
      date: '2029-12-31',
      title: { zh: '首張受約束的執業證書到期', en: 'First practising certificate bound by mandatory CNE expires' },
      note: {
        zh: '於此日到期的執業證書,是首批續期時須符合強制 CNE 要求的證書。較早到期的續期仍屬自願。',
        en: 'Certificates expiring on this date are the first whose renewal requires the mandatory CNE record. Earlier renewals remain voluntary.',
      },
      sourceUrl: 'https://www.nchk.org.hk',
    },
  ],
  lastUpdated: '2026-07-27',
  appStoreUrl: 'https://apps.apple.com/hk/app/vital-nurse-cne/id6772449124',
  webAppUrl: 'https://app.vitalcne.com',
  nchkUrl: 'https://www.nchk.org.hk',
  // Standard disclaimer appended where regulatory detail is not individually verified.
  pendingNote: { zh: '（詳情以護士管理局最新公告為準）', en: '(subject to the latest NCHK announcement)' },
};

// ‼️ Load-bearing — see the header. Never delete.
Object.assign(typeof window !== 'undefined' ? window : globalThis, { CNE_FACTS });
